import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { ApiError } from "../client";
import { useApiClient } from "../context";
import { knownReaderSceneVersion, persistReaderEnvelope, readSceneEnvelope } from "../reader-envelope";
import type { components } from "../schema";
import type { AvailableModel } from "../../features/debug/useModelSelector";

export type Scene = components["schemas"]["Scene"];
export type Action = components["schemas"]["Action"];

export type UsePerformActionResult = UseMutationResult<Scene, ApiError, Action>;

// POST /playlines/{playlineId}/actions を叩き、AI が生成した次シーンを取得する。
// 成功時は currentScene キャッシュを直接書き換えて再フェッチを避け、UI の遷移を即時に反映する。
//
// エラーポリシー（design.md「Revalidation triggers」「State machine」参照）:
//   - 409 Conflict: クライアントが古い状態を握っている可能性が高いため、
//     キャッシュを invalidate して次回描画で最新シーンに同期させる。
//     エラー自体は呼び出し側に伝搬し、UI 側で 409 をバナー非表示とハンドリングする。
//   - 422 InvalidAction: 同様に invalidate して最新シーンへ戻れるようにし、
//     ApiError は伝搬してバナー表示などのフィードバックに使う。
//   - 504 / network 等: シーン状態は変化していない前提なので invalidate せず、
//     ApiError をそのまま伝搬して UI 側の再試行に委ねる。
//
// selectedModel は呼び出し元（PlayingState）が管理する useModelSelector の値を
// 受け取る。hook 内で独立した useModelSelector を呼ぶと UI の選択と乖離するため、
// 引数として外部から注入する設計にしている。
export function usePerformAction(
  playlineId: string,
  selectedModel: AvailableModel,
): UsePerformActionResult {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<Scene, ApiError, Action>({
    mutationFn: async (action) => {
      const { data, error } = await client.POST(
        "/playlines/{playlineId}/actions",
        {
          params: { path: { playlineId } },
          // OAS が Action に model?: string をサポートしているため、
          // 選択中のモデルを付与してバックエンドに伝達する。
          body: { ...action, model: selectedModel },
        },
      );

      if (error !== undefined) {
        // 通常は middleware が ApiError を throw するためここには到達しないが、
        // 型安全のための保険として明示的に throw する。
        throw new ApiError(
          typeof (error as { status?: number }).status === "number"
            ? (error as { status: number }).status
            : 0,
          error as unknown as ApiError["problem"],
        );
      }
      const memoryScene = queryClient.getQueryData<unknown>(["currentScene", playlineId]);
      const expectedVersion = await knownReaderSceneVersion(playlineId, memoryScene);
      return readSceneEnvelope(data, undefined, expectedVersion);
    },
    onSuccess: (scene) => {
      // 次シーンをキャッシュに直接書き込み、useCurrentScene の再フェッチを省略する。
      queryClient.setQueryData<Scene>(["currentScene", playlineId], scene);
      persistReaderEnvelope("scene", playlineId, scene);
    },
    onError: (error) => {
      // 409 / 422 はクライアントとサーバーの状態が乖離している可能性があるため、
      // currentScene キャッシュを無効化して次回参照時に最新を取得させる。
      // 504 や network エラーはサーバー状態が変化していないため invalidate しない。
      if (
        error instanceof ApiError &&
        (error.status === 409 || error.status === 422)
      ) {
        void queryClient.invalidateQueries({
          queryKey: ["currentScene", playlineId],
        });
      }
    },
  });
}
